import React, { useState } from 'react';
import { ArrowLeft, CreditCard, ShieldCheck, AlertCircle } from 'lucide-react';
export const CheckoutView = ({
  cartItems,
  onBack,
  onPlaceOrder,
  appliedPromo,
  onApplyPromo,
  discountAmount,
  initialCustomer,
  initialDelivery,
  deliveryFee: realDeliveryFee,
  taxRate: realTaxRate,
}) => {
  // Prefilled from the real signed-in account and the delivery address/date
  // the user already picked while browsing - never fall back to fake sample
  // data here, since whatever is in these fields gets submitted as the
  // actual order.
  const [firstName, setFirstName] = useState(initialCustomer?.firstName || '');
  const [lastName, setLastName] = useState(initialCustomer?.lastName || '');
  const [companyName, setCompanyName] = useState(
    initialCustomer?.companyName || '',
  );
  const [phone, setPhone] = useState(initialCustomer?.phone || '');
  const [email, setEmail] = useState(initialCustomer?.email || '');
  const [address, setAddress] = useState(initialDelivery?.address || '');
  const [suite, setSuite] = useState('');
  const [deliveryDate, setDeliveryDate] = useState(initialDelivery?.date || '');
  const [deliveryTime, setDeliveryTime] = useState(initialDelivery?.time || '');
  const [instructions, setInstructions] = useState('');
  const [packagingPreference, setPackagingPreference] = useState('individual');
  const [needsCutlery, setNeedsCutlery] = useState(true);
  const [cutleryCount, setCutleryCount] = useState(1);
  const [addressVerified, setAddressVerified] = useState(true);
  const [promoInput, setPromoInput] = useState('');
  const [promoToast, setPromoToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Real delivery fee and tax rate come from the parent (the actual
  // per-restaurant delivery fee lookup and the preset's real tax rate) -
  // this must never fall back to invented numbers, since whatever is
  // shown here is what the customer expects to be charged.
  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  const deliveryFee = realDeliveryFee || 0;
  const taxRate = realTaxRate || 0;
  // Cutlery cost and tax base must mirror the backend's
  // CalculateCateringOrderPrice exactly (cutlery folded into the taxed
  // subtotal, delivery fee also taxed) - otherwise this summary understates
  // what the Stripe payment modal actually charges.
  const CUTLERY_PRICE_DOLLARS = 0.5;
  const cutleryFee = needsCutlery ? cutleryCount * CUTLERY_PRICE_DOLLARS : 0;
  const taxableSubtotal = subtotal + cutleryFee;
  const tax = (taxableSubtotal + deliveryFee) * taxRate;
  const total = Math.max(
    0,
    taxableSubtotal - discountAmount + deliveryFee + tax,
  );
  const handleApplyPromoCode = () => {
    if (!promoInput.trim()) return;
    const success = onApplyPromo(promoInput.trim().toUpperCase());
    if (success) {
      setPromoToast('🎉 Panda Promo code applied! $5 discount saved.');
    } else {
      setPromoToast('Invalid code. Try "PANDA10" for $5 off!');
    }
  };
  const handleSubmit = async e => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!firstName.trim() || !phone.trim() || !address.trim()) {
      alert('Please fill in your name, phone number, and delivery address.');
      return;
    }
    setPromoToast(null);
    setIsSubmitting(true);
    try {
      await onPlaceOrder(
        { firstName, lastName, companyName, phone, email },
        {
          address,
          suite,
          date: deliveryDate,
          time: deliveryTime,
          instructions,
          packagingPreference,
          needsCutlery,
          cutleryCount,
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  return React.createElement(
    'div',
    { className: 'pb-12 space-y-8' },
    React.createElement(
      'div',
      null,
      React.createElement(
        'button',
        {
          onClick: onBack,
          className:
            'inline-flex items-center gap-2 text-xs sm:text-sm font-heading font-bold text-gray-700 hover:text-[#E53935] transition-colors',
        },
        React.createElement(ArrowLeft, { className: 'w-4 h-4' }),
        React.createElement('span', null, 'Back to Menu'),
      ),
    ),
    React.createElement(
      'div',
      { className: 'border-b border-gray-200 pb-4' },
      React.createElement(
        'h1',
        {
          className:
            'text-3xl sm:text-4xl font-heading font-extrabold text-[#202124]',
        },
        'Complete your order',
      ),
      React.createElement(
        'p',
        { className: 'text-xs sm:text-sm text-gray-500 mt-1' },
        'Review your catering and delivery details below for smooth Panda dispatch.',
      ),
    ),
    React.createElement(
      'form',
      {
        onSubmit: handleSubmit,
        className:
          'grid grid-cols-1 gap-8 items-start',
      },
      React.createElement(
        'div',
        { className: 'space-y-6' },
        React.createElement(
          'div',
          {
            className:
              'bg-white rounded-2xl p-5 sm:p-6 border border-gray-200/90 shadow-xs space-y-4',
          },
          React.createElement(
            'h2',
            { className: 'font-heading font-bold text-lg text-[#202124]' },
            'Your details',
          ),
          React.createElement(
            'div',
            { className: 'grid grid-cols-1 sm:grid-cols-2 gap-4' },
            React.createElement(
              'div',
              null,
              React.createElement(
                'label',
                {
                  className:
                    'block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1',
                },
                'First Name',
              ),
              React.createElement('input', {
                type: 'text',
                required: true,
                value: firstName,
                onChange: e => setFirstName(e.target.value),
                className:
                  'w-full px-3.5 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 text-sm text-[#202124] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]',
              }),
            ),
            React.createElement(
              'div',
              null,
              React.createElement(
                'label',
                {
                  className:
                    'block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1',
                },
                'Last Name',
              ),
              React.createElement('input', {
                type: 'text',
                required: true,
                value: lastName,
                onChange: e => setLastName(e.target.value),
                className:
                  'w-full px-3.5 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 text-sm text-[#202124] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]',
              }),
            ),
            React.createElement(
              'div',
              null,
              React.createElement(
                'label',
                {
                  className:
                    'block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1',
                },
                'Company Name (Optional)',
              ),
              React.createElement('input', {
                type: 'text',
                value: companyName,
                onChange: e => setCompanyName(e.target.value),
                className:
                  'w-full px-3.5 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 text-sm text-[#202124] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]',
              }),
            ),
            React.createElement(
              'div',
              null,
              React.createElement(
                'label',
                {
                  className:
                    'block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1',
                },
                'Phone Number',
              ),
              React.createElement('input', {
                type: 'tel',
                required: true,
                value: phone,
                onChange: e => setPhone(e.target.value),
                className:
                  'w-full px-3.5 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 text-sm text-[#202124] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]',
              }),
            ),
            React.createElement(
              'div',
              { className: 'sm:col-span-2' },
              React.createElement(
                'label',
                {
                  className:
                    'block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1',
                },
                'Email',
              ),
              React.createElement('input', {
                type: 'email',
                required: true,
                value: email,
                onChange: e => setEmail(e.target.value),
                className:
                  'w-full px-3.5 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 text-sm text-[#202124] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]',
              }),
            ),
          ),
        ),
      ),
      React.createElement(
        'div',
        { className: 'grid grid-cols-1 md:grid-cols-2 gap-8 items-start' },
        React.createElement(
          'div',
          {
            className:
              'bg-white rounded-2xl p-5 sm:p-6 border border-gray-200/90 shadow-xs space-y-4',
          },
          React.createElement(
            'h2',
            { className: 'font-heading font-bold text-lg text-[#202124]' },
            'Delivery details',
          ),
          React.createElement(
            'div',
            { className: 'grid grid-cols-1 sm:grid-cols-3 gap-4' },
            React.createElement(
              'div',
              { className: 'sm:col-span-2' },
              React.createElement(
                'label',
                {
                  className:
                    'block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1',
                },
                'Delivery Address',
              ),
              React.createElement(
                'div',
                { className: 'relative' },
                React.createElement('input', {
                  type: 'text',
                  required: true,
                  value: address,
                  onChange: e => setAddress(e.target.value),
                  className:
                    'w-full px-3.5 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 text-sm text-[#202124] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]',
                }),
                React.createElement(
                  'button',
                  {
                    type: 'button',
                    onClick: () => setAddressVerified(true),
                    className:
                      'absolute right-2 top-1/2 -translate-y-1/2 text-[10px] bg-red-50 text-[#E53935] font-bold px-2 py-1 rounded-md',
                  },
                  'Verify',
                ),
              ),
            ),
            React.createElement(
              'div',
              null,
              React.createElement(
                'label',
                {
                  className:
                    'block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1',
                },
                'Suite #',
              ),
              React.createElement('input', {
                type: 'text',
                value: suite,
                onChange: e => setSuite(e.target.value),
                placeholder: 'e.g. Suite 400',
                className:
                  'w-full px-3.5 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 text-sm text-[#202124] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]',
              }),
            ),
            React.createElement(
              'div',
              null,
              React.createElement(
                'label',
                {
                  className:
                    'block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1',
                },
                'Delivery Date',
              ),
              React.createElement(
                'div',
                { className: 'relative' },
                React.createElement('input', {
                  type: 'date',
                  value: deliveryDate,
                  onChange: e => setDeliveryDate(e.target.value),
                  className:
                    'w-full px-3.5 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 text-sm text-[#202124] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]',
                }),
              ),
            ),
            React.createElement(
              'div',
              { className: 'sm:col-span-2' },
              React.createElement(
                'label',
                {
                  className:
                    'block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1',
                },
                'Delivery Time',
              ),
              React.createElement(
                'div',
                { className: 'relative' },
                React.createElement('input', {
                  type: 'text',
                  value: deliveryTime,
                  onChange: e => setDeliveryTime(e.target.value),
                  placeholder: '11:30 AM',
                  className:
                    'w-full px-3.5 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 text-sm text-[#202124] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]',
                }),
              ),
              React.createElement(
                'p',
                { className: 'text-[11px] text-[#65A30D] font-medium mt-1' },
                'This is based on Pacific Time (US & Canada)',
              ),
            ),
            React.createElement(
              'div',
              { className: 'sm:col-span-3' },
              React.createElement(
                'label',
                {
                  className:
                    'block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1',
                },
                'Additional delivery instructions',
              ),
              React.createElement('textarea', {
                rows: 2,
                value: instructions,
                onChange: e => setInstructions(e.target.value),
                placeholder:
                  'Gate code, reception drop-off instructions, parking hints...',
                className:
                  'w-full p-3 bg-gray-50/50 rounded-xl border border-gray-200 text-xs text-[#202124] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935] resize-none',
              }),
            ),
          ),
          React.createElement(
            'div',
            {
              className:
                'pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4',
            },
            React.createElement(
              'div',
              { className: 'flex items-center gap-4' },
              React.createElement(
                'label',
                {
                  className:
                    'inline-flex items-center gap-2 cursor-pointer text-xs font-semibold text-[#202124]',
                },
                React.createElement('input', {
                  type: 'radio',
                  name: 'packagingPreference',
                  checked: packagingPreference === 'individual',
                  onChange: () => setPackagingPreference('individual'),
                  className: 'accent-[#E53935] w-4 h-4',
                }),
                React.createElement(
                  'span',
                  null,
                  'Serve Individually Packaged',
                ),
              ),
              React.createElement(
                'label',
                {
                  className:
                    'inline-flex items-center gap-2 cursor-pointer text-xs font-semibold text-[#202124]',
                },
                React.createElement('input', {
                  type: 'radio',
                  name: 'packagingPreference',
                  checked: packagingPreference === 'family',
                  onChange: () => setPackagingPreference('family'),
                  className: 'accent-[#E53935] w-4 h-4',
                }),
                React.createElement('span', null, 'Serve Family Style'),
              ),
            ),
            React.createElement(
              'div',
              { className: 'flex items-center gap-2' },
              React.createElement(
                'label',
                {
                  className:
                    'inline-flex items-center gap-2 cursor-pointer text-xs font-semibold text-[#202124]',
                },
                React.createElement('input', {
                  type: 'checkbox',
                  checked: needsCutlery,
                  onChange: e => setNeedsCutlery(e.target.checked),
                  className: 'accent-[#E53935] w-4 h-4 rounded',
                }),
                React.createElement(
                  'span',
                  null,
                  'I need Cutlery/Plates/Napkins',
                ),
              ),
              needsCutlery &&
                React.createElement('input', {
                  type: 'number',
                  min: '1',
                  max: '50',
                  value: cutleryCount,
                  onChange: e => setCutleryCount(parseInt(e.target.value) || 1),
                  className:
                    'w-14 px-2 py-1 text-xs border border-gray-300 rounded-lg text-center',
                }),
            ),
          ),
        ),
        React.createElement(
          'div',
          { className: 'space-y-4' },
          React.createElement(
            'div',
            {
              className:
                'bg-white rounded-2xl border border-gray-200/90 shadow-sm overflow-hidden',
            },
          React.createElement(
            'div',
            {
              className:
                'p-4 sm:p-5 border-b border-gray-100 flex items-center justify-between bg-panda-tint/40',
            },
            React.createElement(
              'h3',
              { className: 'font-heading font-bold text-base text-[#202124]' },
              'Order Details',
            ),
            React.createElement(
              'span',
              {
                className:
                  'w-5 h-5 rounded-full bg-[#E53935] text-white text-xs font-bold flex items-center justify-center',
              },
              cartItems.reduce((s, i) => s + i.quantity, 0),
            ),
          ),
          React.createElement(
            'div',
            {
              className:
                'p-3.5 bg-red-50/70 border-b border-red-100 flex items-start gap-2 text-xs text-[#E53935]',
            },
            React.createElement(AlertCircle, {
              className: 'w-4 h-4 flex-shrink-0 mt-0.5',
            }),
            React.createElement(
              'div',
              null,
              React.createElement(
                'p',
                { className: 'font-semibold leading-snug' },
                'Panda Express Delivery Route Confirmed',
              ),
              React.createElement(
                'p',
                { className: 'text-[11px] text-gray-600 mt-0.5' },
                'Your delivery address is within our speedy scooter zone (15-25 min ETA).',
              ),
            ),
          ),
          React.createElement(
            'div',
            { className: 'p-4 sm:p-5 space-y-3 max-h-60 overflow-y-auto' },
            cartItems.map(item =>
              React.createElement(
                'div',
                {
                  key: item.id,
                  className:
                    'flex items-center justify-between text-xs sm:text-sm',
                },
                React.createElement(
                  'div',
                  { className: 'flex items-center gap-2' },
                  React.createElement(
                    'span',
                    {
                      className:
                        'w-5 h-5 rounded-full bg-panda-tint text-[#E53935] text-xs font-bold flex items-center justify-center',
                    },
                    item.quantity,
                  ),
                  React.createElement(
                    'span',
                    { className: 'font-semibold text-[#202124]' },
                    item.name,
                  ),
                ),
                React.createElement(
                  'span',
                  { className: 'font-bold text-gray-800' },
                  '$',
                  (item.price * item.quantity).toFixed(2),
                ),
              ),
            ),
          ),
          React.createElement(
            'div',
            {
              className:
                'p-4 sm:p-5 border-t border-gray-100 space-y-2 text-xs text-gray-600 bg-gray-50/30',
            },
            React.createElement(
              'div',
              { className: 'flex justify-between' },
              React.createElement('span', null, 'Subtotal'),
              React.createElement(
                'span',
                { className: 'font-semibold text-[#202124]' },
                '$',
                subtotal.toFixed(2),
              ),
            ),
            cutleryFee > 0 &&
              React.createElement(
                'div',
                { className: 'flex justify-between' },
                React.createElement(
                  'span',
                  null,
                  `Cutlery/Plates/Napkins (${cutleryCount} \u00D7 $0.50)`,
                ),
                React.createElement(
                  'span',
                  { className: 'font-semibold text-[#202124]' },
                  '$',
                  cutleryFee.toFixed(2),
                ),
              ),
            React.createElement(
              'div',
              { className: 'flex justify-between' },
              React.createElement(
                'span',
                { className: 'flex items-center gap-1' },
                'Delivery Fee \u24D8',
              ),
              React.createElement(
                'span',
                { className: 'font-semibold text-[#202124]' },
                deliveryFee === 0
                  ? React.createElement(
                      'span',
                      { className: 'text-[#65A30D] font-bold' },
                      'FREE',
                    )
                  : `$${deliveryFee.toFixed(2)}`,
              ),
            ),
            discountAmount > 0 &&
              React.createElement(
                'div',
                { className: 'flex justify-between text-[#65A30D] font-bold' },
                React.createElement('span', null, 'Panda Promo Discount'),
                React.createElement(
                  'span',
                  null,
                  '-$',
                  discountAmount.toFixed(2),
                ),
              ),
            React.createElement(
              'div',
              { className: 'flex justify-between' },
              React.createElement(
                'span',
                null,
                `Sales Tax (${(taxRate * 100).toFixed(3)}%)`,
              ),
              React.createElement(
                'span',
                { className: 'font-semibold text-[#202124]' },
                '$',
                tax.toFixed(2),
              ),
            ),
            React.createElement(
              'div',
              {
                className:
                  'pt-2 border-t border-gray-200 flex justify-between items-center text-base font-bold text-[#202124]',
              },
              React.createElement('span', null, 'Total'),
              React.createElement(
                'span',
                { className: 'text-[#E53935] font-brand text-2xl' },
                '$',
                total.toFixed(2),
              ),
            ),
          ),
          React.createElement(
            'div',
            { className: 'p-4 sm:p-5 border-t border-gray-100 bg-white' },
            React.createElement(
              'div',
              { className: 'flex gap-2' },
              React.createElement('input', {
                type: 'text',
                placeholder: 'Enter promo code',
                value: promoInput,
                onChange: e => setPromoInput(e.target.value),
                className:
                  'flex-1 px-3 py-2 text-xs bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#E53935]',
              }),
              React.createElement(
                'button',
                {
                  type: 'button',
                  onClick: handleApplyPromoCode,
                  className:
                    'px-4 py-2 rounded-xl bg-panda-tint hover:bg-panda-soft text-panda-dark font-heading font-bold text-xs transition-colors',
                },
                'Apply',
              ),
            ),
            promoToast &&
              React.createElement(
                'p',
                { className: 'text-[11px] text-[#65A30D] mt-1.5 font-medium' },
                promoToast,
              ),
            React.createElement(
              'button',
              {
                type: 'submit',
                id: 'checkout-pay-btn',
                disabled: isSubmitting || cartItems.length === 0,
                className:
                  'w-full mt-4 py-3.5 px-4 rounded-xl bg-gradient-to-r from-panda-red to-panda-dark hover:from-panda-dark hover:to-panda-dark text-white font-heading font-extrabold text-base shadow-md shadow-panda-red/30 active:scale-98 transition-all flex items-center justify-center gap-2.5 disabled:opacity-50',
              },
              React.createElement(
                'span',
                null,
                isSubmitting
                  ? 'Processing Payment...'
                  : `Pay $${total.toFixed(2)}`,
              ),
              React.createElement(CreditCard, {
                className: 'w-5 h-5 text-white',
              }),
            ),
            React.createElement(
              'div',
              {
                className:
                  'flex items-center justify-center gap-1.5 text-[11px] text-gray-500 mt-2',
              },
              React.createElement(ShieldCheck, {
                className: 'w-3.5 h-3.5 text-[#65A30D]',
              }),
              React.createElement(
                'span',
                null,
                '256-bit Encrypted & Guaranteed Panda Freshness',
              ),
            ),
          ),
        ),
      ),
      ),
    ),
  );
};
